import { ReferenceDirection } from '@ardoq/api-types';
import {
  ChartWindowState,
  LABEL_MARGIN,
  labelHeight,
  Margin,
  NumericRange,
  Rect,
  TICK_LENGTH,
} from '@ardoq/graph';
import { groupBy } from 'lodash';
import {
  formatNumber,
  getCurrencySymbol,
  isCurrencyFormat,
  Locale,
} from '@ardoq/locale';
import { getRoundedInterval, Point } from '@ardoq/math';
import { isBubbleChartFieldInfo } from './predicates';
import {
  getAvailableFieldsOfType,
  type AvailableReferenceModeFields,
} from './availableFieldsOfType';
import {
  DEFAULT_BUBBLE_CHART_VIEW_SETTINGS,
  DEFAULT_BUBBLE_RADIUS,
  DISPLAYED_LABELS_LIMIT,
  QUADRANT_INFO_MAP,
  TITLE_MARGIN,
} from './consts';
import {
  BubbleChartAxisProps,
  BubbleChartBubbleModel,
  BubbleChartDataPointModel,
  BubbleChartDomains,
  BubbleChartGridMode,
  BubbleChartQuadrantSettings,
  BubbleChartScalers,
  BubbleChartSelectedFieldInfo,
  BubbleChartViewSettings,
  ComponentTypeRegistry,
  FieldData,
  GetAxisPropsArgs,
  IncludeDescendants,
  Quadrant,
  QuadrantInfo,
  QuadrantOverlayProps,
  ScalingFunction,
  TickMarkProps,
  TimeLayoutLabels,
} from './types';

export const resolveIncludeAllDescendants = (
  value: BubbleChartViewSettings['includeAllDescendants']
): IncludeDescendants =>
  value === true ? 'all' : value === false ? 'direct' : value;

/** we'll be clamping to these max safe numbers to avoid infinite loops caused by inability to increment and compare with decimal precision. */
const MAX_SAFE_NUMBER = (Number.MAX_SAFE_INTEGER + 1) / 16 - 1;
const MIN_SAFE_NUMBER = -MAX_SAFE_NUMBER;
const clampToSafeNumber = (value: number) =>
  Math.max(MIN_SAFE_NUMBER + 1, Math.min(MAX_SAFE_NUMBER - 1, value));
const clampToSafeNumbers = ([min, max]: NumericRange): NumericRange => [
  clampToSafeNumber(min),
  clampToSafeNumber(max),
];
const makeFinite = (range: NumericRange): NumericRange => [
  Number.isFinite(range[0]) ? range[0] : 0,
  Number.isFinite(range[1]) ? range[1] : 0,
];
const inflateZeroRange = (range: NumericRange): NumericRange =>
  range[0] === range[1] ? [range[0] - 1, range[1] + 1] : range;

const getDataRange = (
  data: BubbleChartDataPointModel[],
  fieldName: 'x' | 'y' | 'radius'
): NumericRange => [
  Math.min(...data.map(item => item[fieldName])),
  Math.max(...data.map(item => item[fieldName])),
];

const getRange = (
  data: BubbleChartDataPointModel[],
  fieldName: 'x' | 'y' | 'radius'
): NumericRange =>
  inflateZeroRange(
    clampToSafeNumbers(makeFinite(getDataRange(data, fieldName)))
  );

const getRoundedRange = (range: NumericRange): NumericRange => {
  const interval = getRoundedInterval(range);
  return [
    interval * Math.floor(range[0] / interval),
    interval * Math.ceil(range[1] / interval),
  ];
};

const bubbleChartXYDomains = (
  viewSettings: BubbleChartViewSettings,
  data: BubbleChartDataPointModel[]
) => {
  const {
    background,
    customQuadrantSettings: viewSettingsCustomQuadrantSettings,
  } = viewSettings;
  const timeMode = background === BubbleChartGridMode.TIME;
  const customQuadrants = background === BubbleChartGridMode.CUSTOM;

  const customQuadrantSettings =
    viewSettingsCustomQuadrantSettings ||
    DEFAULT_BUBBLE_CHART_VIEW_SETTINGS.customQuadrantSettings;

  const customQuadrantsXRange: NumericRange = [
    customQuadrantSettings.xMinimum,
    customQuadrantSettings.xMaximum,
  ];
  const customQuadrantsYRange: NumericRange = [
    customQuadrantSettings.yMinimum,
    customQuadrantSettings.yMaximum,
  ];

  const xNeedsAutoRange =
    !timeMode &&
    (!customQuadrants ||
      !Number.isFinite(customQuadrantsXRange[0]) ||
      !Number.isFinite(customQuadrantsXRange[1]));
  const yNeedsAutoRange =
    !timeMode &&
    (!customQuadrants ||
      !Number.isFinite(customQuadrantsYRange[0]) ||
      !Number.isFinite(customQuadrantsYRange[1]));

  const invalidRange: NumericRange = [NaN, NaN];
  const xAutoRange = xNeedsAutoRange
    ? getRoundedRange(getRange(data, 'x'))
    : invalidRange;
  const yAutoRange = yNeedsAutoRange
    ? getRoundedRange(getRange(data, 'y'))
    : invalidRange;
  if (customQuadrants) {
    customQuadrantsXRange[0] = Number.isFinite(customQuadrantsXRange[0])
      ? customQuadrantsXRange[0]
      : xAutoRange[0];
    customQuadrantsXRange[1] = Number.isFinite(customQuadrantsXRange[1])
      ? customQuadrantsXRange[1]
      : xAutoRange[1];
    customQuadrantsYRange[0] = Number.isFinite(customQuadrantsYRange[0])
      ? customQuadrantsYRange[0]
      : yAutoRange[0];
    customQuadrantsYRange[1] = Number.isFinite(customQuadrantsYRange[1])
      ? customQuadrantsYRange[1]
      : yAutoRange[1];
  }
  const x: NumericRange = timeMode
    ? [1, 5]
    : customQuadrants
      ? inflateZeroRange(makeFinite(customQuadrantsXRange))
      : xAutoRange;
  const y: NumericRange = timeMode
    ? [1, 5]
    : customQuadrants
      ? inflateZeroRange(makeFinite(customQuadrantsYRange))
      : yAutoRange;
  return { x, y };
};

type GetBubbleChartDomainsArgs = {
  data: BubbleChartDataPointModel[];
  viewSettings: BubbleChartViewSettings;
};
export const getBubbleChartDomains = ({
  data,
  viewSettings,
}: GetBubbleChartDomainsArgs): BubbleChartDomains => {
  return {
    ...bubbleChartXYDomains(viewSettings, data),
    radius: getDataRange(data, 'radius'),
    labeledBubblesRadius: getDataRange(
      data.slice(0, DISPLAYED_LABELS_LIMIT),
      'radius'
    ),
  };
};

/** removes any non-breaking spaces from the formatted number, because spaces in axis labels look bad. weird cultures like Norway use a space as the thousands separator */
export const formatAxisLabel = (value: number, locale: Locale) =>
  formatNumber(value, 'default', locale).replaceAll('\xa0', '');

/**
 * Sorts data points by clusters and organizes them so that:
 * 1. The largest points from each cluster are displayed first
 * 2. The first DISPLAYED_LABELS_LIMIT points are used for displaying labels
 *
 * @returns Sorted array of data points
 */
export const sortDataPointsByCluster = (
  dataPoints: BubbleChartDataPointModel[]
): BubbleChartDataPointModel[] => {
  if (dataPoints.length <= DISPLAYED_LABELS_LIMIT) {
    return dataPoints.sort((a, b) => b.radius - a.radius);
  }

  // Group points by coordinates and radius (create clusters)
  const clusters = Object.values(
    groupBy(
      dataPoints,
      dataPoint => `${dataPoint.x},${dataPoint.y},${dataPoint.radius}`
    )
  ).sort(([a], [b]) => b.radius - a.radius);

  // Collect points for labels, selecting them by levels
  const dataPointsForLabels: BubbleChartDataPointModel[] = [];

  for (
    let clusterDepth = 0;
    dataPointsForLabels.length < DISPLAYED_LABELS_LIMIT;
    clusterDepth++
  ) {
    let dataPointAdded = false;

    for (const cluster of clusters) {
      if (clusterDepth < cluster.length) {
        dataPointsForLabels.push(cluster[clusterDepth]);
        dataPointAdded = true;

        if (dataPointsForLabels.length >= DISPLAYED_LABELS_LIMIT) {
          break;
        }
      }
    }
    // Break the loop, because there are no datapoints on next cluster level
    if (!dataPointAdded) {
      break;
    }
  }

  // Sort labeled points by radius (from largest to smallest)
  dataPointsForLabels.sort((a, b) => b.radius - a.radius);

  // Create a set of labeled point IDs for quick lookup
  const labeledPointIds = new Set(dataPointsForLabels);

  // Collect remaining points
  const remainingDataPoints: BubbleChartDataPointModel[] = [];
  clusters.forEach(cluster => {
    cluster.forEach(point => {
      if (!labeledPointIds.has(point)) {
        remainingDataPoints.push(point);
      }
    });
  });

  return [...dataPointsForLabels, ...remainingDataPoints];
};

export const findTypeIdByComponentId = (
  componentId: string,
  registry: ComponentTypeRegistry
) => {
  for (const [typeId, { components }] of registry) {
    if (components.has(componentId)) {
      return typeId;
    }
  }
  return null;
};

const getFieldLabel = (locale: Locale, field?: FieldData) =>
  field && isCurrencyFormat(field.numberFormatOptions)
    ? `${field.label} (${getCurrencySymbol(field.numberFormatOptions, locale)})`
    : field?.label || '';

export const getAxisTitlesForComponentChart = (
  viewSettings: BubbleChartViewSettings,
  numericfields: FieldData[],
  locale: Locale
) => {
  const { selectedFieldNameX, selectedFieldNameY } = viewSettings;
  const [xField, yField] = [selectedFieldNameX, selectedFieldNameY].map(
    fieldName => numericfields.find(field => field.name === fieldName)
  );

  return {
    xAxisTitle: getFieldLabel(locale, xField),
    yAxisTitle: getFieldLabel(locale, yField),
  };
};

export const safeViewNodeDimensions = (
  selectedXValue: unknown,
  selectedYValue: unknown,
  selectedRadiusValue: unknown
) => ({
  x: typeof selectedXValue === 'number' ? selectedXValue : 0,
  y: typeof selectedYValue === 'number' ? selectedYValue : 0,
  radius:
    typeof selectedRadiusValue === 'number'
      ? selectedRadiusValue
      : DEFAULT_BUBBLE_RADIUS,
});

const resolveAxisFieldLabel = (
  field: string | BubbleChartSelectedFieldInfo | null,
  referenceModeFields: AvailableReferenceModeFields
): string => {
  if (!isBubbleChartFieldInfo(field)) {
    return '';
  }

  const fieldData = getAvailableFieldsOfType(
    field.type,
    referenceModeFields
  ).find(f => f.name === field.fieldName);
  return fieldData?.label || field.fieldName;
};

export const getAxisTitlesForReferenceChart = (
  viewSettings: BubbleChartViewSettings,
  referenceModeFields: AvailableReferenceModeFields
): { xAxisTitle: string; yAxisTitle: string } => {
  const { selectedFieldNameX, selectedFieldNameY } = viewSettings;

  return {
    xAxisTitle: resolveAxisFieldLabel(selectedFieldNameX, referenceModeFields),
    yAxisTitle: resolveAxisFieldLabel(selectedFieldNameY, referenceModeFields),
  };
};

export const getAxisTitlesForReferenceChartWithDirection = (
  viewSettings: BubbleChartViewSettings
) => {
  const {
    selectedReferenceDirection,
    selectedFieldNameX,
    selectedFieldNameY,
    selectedReferenceTypeName,
  } = viewSettings;

  const xFieldLabel = isBubbleChartFieldInfo(selectedFieldNameX)
    ? selectedFieldNameX.fieldName
    : '';
  const yFieldLabel = isBubbleChartFieldInfo(selectedFieldNameY)
    ? selectedFieldNameY.fieldName
    : '';
  const referenceArrow =
    selectedReferenceDirection === ReferenceDirection.OUTGOING ? '→' : '←';

  return {
    xAxisTitle: `${selectedReferenceTypeName} ${referenceArrow} ${xFieldLabel}`,
    yAxisTitle: `${selectedReferenceTypeName} ${referenceArrow} ${yFieldLabel}`,
  };
};

const DEFAULT_TIME_LAYOUT_LABELS: TimeLayoutLabels = {
  tolerateLabel: 'tolerate',
  investLabel: 'invest',
  migrateLabel: 'migrate',
  eliminateLabel: 'eliminate',
};

export const getQuadrantSettings = (
  background: BubbleChartGridMode,
  customQuadrantSettings: BubbleChartQuadrantSettings | null,
  timeLabels?: TimeLayoutLabels
): BubbleChartQuadrantSettings | null => {
  if (background === BubbleChartGridMode.TIME) {
    const { tolerateLabel, investLabel, eliminateLabel, migrateLabel } = {
      ...DEFAULT_TIME_LAYOUT_LABELS,
      ...timeLabels,
    };
    return {
      xMinimum: 1,
      xMaximum: 5,
      yMinimum: 1,
      yMaximum: 5,
      labels: [tolerateLabel, investLabel, eliminateLabel, migrateLabel],
    };
  }

  if (background === BubbleChartGridMode.CUSTOM) {
    return customQuadrantSettings;
  }

  return null;
};

export const getDataPointsByQuadrant = (
  data: BubbleChartDataPointModel[],
  domain: BubbleChartDomains
) =>
  data.reduce<Record<Quadrant, BubbleChartDataPointModel[]>>(
    (acc, point) => {
      const x = point.x;
      const y = point.y;
      const centerX = (domain.x[0] + domain.x[1]) / 2;
      const centerY = (domain.y[0] + domain.y[1]) / 2;

      if (x >= centerX && y >= centerY) {
        acc.topRight.push(point);
      }
      if (x <= centerX && y >= centerY) {
        acc.topLeft.push(point);
      }
      if (x <= centerX && y <= centerY) {
        acc.bottomLeft.push(point);
      }
      if (x >= centerX && y <= centerY) {
        acc.bottomRight.push(point);
      }
      return acc;
    },
    { topRight: [], topLeft: [], bottomLeft: [], bottomRight: [] }
  );

const getQuadrantInfo = (quadrant: Quadrant): QuadrantInfo =>
  QUADRANT_INFO_MAP[quadrant];

type CalculateAxisOverflowArgs = {
  point: BubbleChartDataPointModel;
  centerX: number;
  centerY: number;
  domainWidth: number;
  domainHeight: number;
  scalers: BubbleChartScalers;
};

const calculateAxisOverflow = ({
  point,
  centerX,
  centerY,
  domainWidth,
  domainHeight,
  scalers,
}: CalculateAxisOverflowArgs): number => {
  let maxOverflow = 0;

  // Convert coordinates to pixels using scalers
  const pixelX = scalers.x(point.x);
  const pixelY = scalers.y(point.y);
  const pixelRadius = scalers.radius(point.radius);
  const pixelCenterX = scalers.x(centerX);
  const pixelCenterY = scalers.y(centerY);

  // Check X axis overflow
  const distanceToVerticalAxis = Math.abs(pixelX - pixelCenterX);
  if (pixelRadius > distanceToVerticalAxis) {
    const overflowPixels = pixelRadius - distanceToVerticalAxis;
    const halfDomainWidthPixels = Math.abs(
      scalers.x(centerX + domainWidth / 2) - scalers.x(centerX)
    );
    const overflow = overflowPixels / halfDomainWidthPixels;
    maxOverflow = Math.max(maxOverflow, overflow);
  }

  // Check Y axis overflow
  const distanceToHorizontalAxis = Math.abs(pixelY - pixelCenterY);
  if (pixelRadius > distanceToHorizontalAxis) {
    const overflowPixels = pixelRadius - distanceToHorizontalAxis;
    const halfDomainHeightPixels = Math.abs(
      scalers.y(centerY + domainHeight / 2) - scalers.y(centerY)
    );
    const overflow = overflowPixels / halfDomainHeightPixels;
    maxOverflow = Math.max(maxOverflow, overflow);
  }

  return maxOverflow;
};

const clampWindowCenter = (windowCenter: Point, windowScale: number): Point => {
  const centerMax = 1 - windowScale / 2;
  const centerMin = windowScale / 2;

  const clampedX = Math.max(centerMin, Math.min(centerMax, windowCenter[0]));
  const clampedY = Math.max(centerMin, Math.min(centerMax, windowCenter[1]));

  return [clampedX, clampedY];
};

type QuadrantData = Record<Quadrant, BubbleChartDataPointModel[]>;
type CreateQuadrantWindowStatesArgs = {
  quadrantData: QuadrantData;
  domains: BubbleChartDomains;
  scalers: BubbleChartScalers;
};
/**
 * Creates optimal window states for each quadrant accounting for overflowing bubbles
 *
 * Logic:
 * - If quadrant is "clean" (bubbles don't cross central axes): scale = 0.45
 * - If there are intersections: scale increases up to maximum 1.0
 * - Window center is always positioned at quadrant corner
 */
export const createQuadrantWindowStates = ({
  quadrantData,
  domains,
  scalers,
}: CreateQuadrantWindowStatesArgs): Record<Quadrant, ChartWindowState> => {
  const centerX = (domains.x[0] + domains.x[1]) / 2;
  const centerY = (domains.y[0] + domains.y[1]) / 2;
  const domainWidth = domains.x[1] - domains.x[0];
  const domainHeight = domains.y[1] - domains.y[0];

  const createWindowStateForQuadrant = (
    quadrant: Quadrant,
    points: BubbleChartDataPointModel[]
  ): ChartWindowState => {
    const baseScale = 0.45;
    let scale = baseScale;

    const quadrantInfo = getQuadrantInfo(quadrant);
    let maxOverflowRatio = 0;

    for (const point of points) {
      const overflowRatio = calculateAxisOverflow({
        point: point,
        centerX: centerX,
        centerY: centerY,
        domainWidth: domainWidth,
        domainHeight: domainHeight,
        scalers: scalers,
      });
      maxOverflowRatio = Math.max(maxOverflowRatio, overflowRatio);
    }

    if (maxOverflowRatio > 0) {
      const bufferedRatio = maxOverflowRatio * 1.2;
      scale = Math.min(1.0, baseScale * (1 + bufferedRatio));
    }

    const desiredWindowCenter: Point = [
      0.5 + quadrantInfo.centerOffsetX,
      0.5 + quadrantInfo.centerOffsetY,
    ];

    const clampedWindowCenter = clampWindowCenter(desiredWindowCenter, scale);

    return {
      windowCenter: clampedWindowCenter,
      windowScale: scale,
    };
  };

  return {
    [Quadrant.TOP_LEFT]: createWindowStateForQuadrant(
      Quadrant.TOP_LEFT,
      quadrantData[Quadrant.TOP_LEFT]
    ),
    [Quadrant.TOP_RIGHT]: createWindowStateForQuadrant(
      Quadrant.TOP_RIGHT,
      quadrantData[Quadrant.TOP_RIGHT]
    ),
    [Quadrant.BOTTOM_LEFT]: createWindowStateForQuadrant(
      Quadrant.BOTTOM_LEFT,
      quadrantData[Quadrant.BOTTOM_LEFT]
    ),
    [Quadrant.BOTTOM_RIGHT]: createWindowStateForQuadrant(
      Quadrant.BOTTOM_RIGHT,
      quadrantData[Quadrant.BOTTOM_RIGHT]
    ),
  };
};

export const getBoundingBox = ({ x, y, radius }: BubbleChartBubbleModel) =>
  new Rect(x - radius, y - radius, 2 * radius, 2 * radius);

export const applyInteractiveStatesToDataPoint = (
  higlightedItemsToLabels: Map<string, Point | null>,
  dataPoint: BubbleChartBubbleModel
) => {
  const isBlurred = !higlightedItemsToLabels.has(dataPoint.uniqueId);

  return isBlurred === dataPoint.isBlurred
    ? dataPoint
    : {
        ...dataPoint,
        isBlurred,
      };
};

export const getYAxisProps = ({
  margin,
  height,
  width,
  ticks,
  scaler,
  grid,
  title,
  locale,
}: GetAxisPropsArgs): BubbleChartAxisProps => {
  const gridLineLength = grid ? `${width}` : `-${TICK_LENGTH}`;
  const titleX = -margin.left + labelHeight / 2 + TITLE_MARGIN;
  const titleY = height / 2;

  const axisLabelX = -(TICK_LENGTH + LABEL_MARGIN);

  const tickProps = ticks.map((tick): TickMarkProps => ({
    transform: `translate(0, ${scaler(tick)})`,
    gridLineLength,
    tickLabel: formatAxisLabel(tick, locale),
    labelOffset: axisLabelX,
  }));

  const axisTitleProps = {
    x: titleX,
    y: titleY,
    transform: `rotate(-90, ${titleX}, ${titleY})`,
  };

  const axisTransform = `translate(${margin.left}, ${margin.top})`;
  const axisLinePath = `M-${TICK_LENGTH},0H0V${height}H-${TICK_LENGTH}`;

  return {
    title,
    axisTransform,
    axisLinePath,
    tickProps,
    axisTitleProps,
  };
};

const getTimeCenters = (
  xRange: NumericRange,
  yRange: NumericRange,
  scaleX: ScalingFunction,
  scaleY: ScalingFunction
) => {
  const timeCenterX = scaleX(xRange[0] + (xRange[1] - xRange[0]) / 2);
  const timeCenterY = scaleY(yRange[0] + (yRange[1] - yRange[0]) / 2);
  return { timeCenterX, timeCenterY };
};

const getClipPathIds = (viewInstanceId: string) => ({
  topRightQuadrantClipPath: `topRightQuadrantClipPath-${viewInstanceId}`,
  bottomRightQuadrantClipPath: `bottomRightQuadrantClipPath-${viewInstanceId}`,
  bottomLeftQuadrantClipPath: `bottomLeftQuadrantClipPath-${viewInstanceId}`,
  topLeftQuadrantClipPath: `topLeftQuadrantClipPath-${viewInstanceId}`,
});

const getQuadrantLabelTextCoordinates = (
  width: number,
  height: number,
  timeCenterX: number,
  timeCenterY: number
) => {
  const leftQuadrantsLabelX = Math.min(width, timeCenterX) / 2;
  const rightQuadrantsLabelX = width - (width - Math.max(0, timeCenterX)) / 2;
  const topQuadrantsLabelY = Math.min(height, timeCenterY) / 2;
  const bottomQuadrantsLabelY =
    height - (height - Math.max(0, timeCenterY)) / 2;
  return {
    leftQuadrantsLabelX,
    rightQuadrantsLabelX,
    topQuadrantsLabelY,
    bottomQuadrantsLabelY,
  };
};

export type GetQuadrantOverlayPropsArgs = {
  chartWidth: number;
  chartHeight: number;
  margin: Margin;
  scaleX: ScalingFunction;
  scaleY: ScalingFunction;
  viewInstanceId: string;
  xRange: NumericRange;
  yRange: NumericRange;
  quadrantSettings: BubbleChartQuadrantSettings;
  chartAreaClipPathUrl: string;
  selectedQuadrant: Quadrant | null;
};
export const getQuadrantOverlayProps = ({
  chartWidth,
  chartHeight,
  scaleX,
  scaleY,
  viewInstanceId,
  xRange,
  yRange,
  margin,
  chartAreaClipPathUrl,
  quadrantSettings,
  selectedQuadrant,
}: GetQuadrantOverlayPropsArgs): QuadrantOverlayProps => {
  const { timeCenterX, timeCenterY } = getTimeCenters(
    xRange,
    yRange,
    scaleX,
    scaleY
  );

  const labelTextCoordinates = getQuadrantLabelTextCoordinates(
    chartWidth,
    chartHeight,
    timeCenterX,
    timeCenterY
  );

  const clipPathIds = getClipPathIds(viewInstanceId);

  return {
    chartWidth,
    chartHeight,
    margin,
    timeCenterX,
    timeCenterY,
    chartAreaClipPathUrl,
    labelTexts: {
      topLeftLabelText: quadrantSettings.labels[0],
      topRightLabelText: quadrantSettings.labels[1],
      bottomLeftLabelText: quadrantSettings.labels[2],
      bottomRightLabelText: quadrantSettings.labels[3],
    },
    labelTextCoordinates,
    clipPathIds,
    selectedQuadrant,
  };
};
